import { memo, useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Form, Button, Input } from 'antd';

import AuthShell from '../../components/auth-shell';
import shell from '../../components/auth-shell/index.module.css';

interface Props {
  isLoading: boolean;
  resetForm: boolean;
  onResetPassword: (password: string, token: string) => void;
  onResetPasswordRequest: (email: string) => void;
}

/**
 * Both halves of the reset flow. Without a token in the URL this asks for an
 * email address and sends a link; with one it takes the new password.
 *
 * The heading names which half you are in. It used to read "Reset your
 * password" in both, which is the state the flow is in rather than the thing
 * being asked of you -- and identical wording either side of an email link
 * gives no confirmation that the link worked.
 */
const ResetPassword = ({
  isLoading,
  resetForm,
  onResetPasswordRequest,
  onResetPassword,
}: Props) => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    if (resetForm) {
      form.setFieldValue('email', '');
    }
  }, [resetForm]);

  const onFinish = useCallback(() => {
    if (token) {
      onResetPassword(form.getFieldValue('password'), token);
    } else {
      onResetPasswordRequest(form.getFieldValue('email'));
    }
  }, [token, form, onResetPasswordRequest, onResetPassword]);

  const passwordRules = [
    { required: true, message: 'Please input your password!' },
    { min: 8, message: 'Password must be at least 8 characters long!' },
    {
      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
      message:
        'Password must include uppercase, lowercase, number, and special character!',
    },
  ];

  return (
    <AuthShell
      title={token ? 'Choose a new password' : 'Reset your password'}
      subtitle={
        token
          ? 'Pick something at least 8 characters long, with an uppercase letter, a number and a symbol.'
          : 'Enter your email address and we will send you a link to create a new password.'
      }
      footer={
        <Link to="/auth/login" className={shell.link}>
          Back to sign in
        </Link>
      }
    >
      <Form
        form={form}
        style={{ width: '100%' }}
        onFinish={onFinish}
        layout="vertical"
      >
        {token ? (
          <>
            <Form.Item
              name="password"
              label="New password"
              rules={passwordRules}
              hasFeedback
            >
              <Input.Password placeholder="••••••••" size="large" />
            </Form.Item>
            <Form.Item
              name="confirm"
              label="Confirm new password"
              dependencies={['password']}
              hasFeedback
              rules={[
                ...passwordRules,
                ({ getFieldValue }) => ({
                  validator(_: unknown, value: string) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        'The new password that you entered do not match!',
                      ),
                    );
                  },
                }),
              ]}
            >
              <Input.Password placeholder="••••••••" size="large" />
            </Form.Item>
          </>
        ) : (
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input your email' },
              { type: 'email' },
            ]}
          >
            <Input placeholder="you@company.com" size="large" />
          </Form.Item>
        )}
        <Form.Item>
          <Button
            type="primary"
            size="large"
            block
            htmlType="submit"
            loading={isLoading}
            className={shell.submitBtn}
          >
            {token ? 'Save new password' : 'Send reset link'}
          </Button>
        </Form.Item>
      </Form>
    </AuthShell>
  );
};

export default memo(ResetPassword);
